import { useEffect, useRef, useState, type ReactNode } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { MainLayout } from "~/layout/MainLayout";
import { useDraftSync } from "~/shared/hooks/useDraftSync";
import type { FormRegistryEntry } from "~/shared/utils/formRegistry";
import { useSignUpStore } from "~/stores";
import { useFunnelStore } from "~/stores/useFunnelStore";
import { funnelTracker } from "../../utils/funnelTracking";

interface SignUpFlowShellProps {
  formSteps: FormRegistryEntry[];
  flowVersion: string;
  terminalRoute: string;
  /** Fired once, after the first page-view is tracked (patient uses this for GA4). */
  onFirstPageView?: () => void;
  /** The landing block; receives the start handler to wire its CTA. */
  renderIntro: (onStart: () => void) => ReactNode;
}

export const SignUpFlowShell = ({
  formSteps,
  flowVersion,
  terminalRoute,
  onFirstPageView,
  renderIntro,
}: SignUpFlowShellProps) => {
  const navigate = useNavigate();
  const {
    reset,
    setPatientField,
    currentStepIndex,
    setCurrentStepIndex,
    patient,
    appointmentBooked,
  } = useSignUpStore();

  // If a previous flow completed (appointment booked), reset all state so stale
  // patient data can't leak into the next flow (e.g. navigating back to sign-up
  // without clicking "Book Another Patient").
  useEffect(() => {
    if (appointmentBooked) {
      sessionStorage.removeItem("signup-form-storage");
      reset();
      useFunnelStore.getState().resetSession();
      setShowForm(false);
    }
  }, []);

  const hasInProgressFlow =
    !appointmentBooked && currentStepIndex > 0 && !!patient.conditions?.length;
  const [showForm, setShowForm] = useState(hasInProgressFlow);
  const { syncDraft, flushDraftSync, finalizeDraft } = useDraftSync();
  const hasTrackedPageViewRef = useRef(false);

  useEffect(() => {
    if (showForm) {
      useFunnelStore.getState().setStepPosition(currentStepIndex);
    }
  }, [currentStepIndex, showForm]);

  useEffect(() => {
    funnelTracker.setFlowVersion(flowVersion);
    if (!hasTrackedPageViewRef.current) {
      hasTrackedPageViewRef.current = true;
      funnelTracker.trackPageView();
      onFirstPageView?.();
    }
    return () => {
      funnelTracker.clearFlowVersion();
    };
  }, []);

  const onNext = async () => {
    if (currentStepIndex >= formSteps.length - 1) {
      if (patient.id) {
        await flushDraftSync();
        navigate(terminalRoute);
        return;
      }
      try {
        // One round-trip: syncs the final payload, creates the patient, and
        // assigns the provider server-side.
        const result = await finalizeDraft();
        setPatientField("id", result.id);
        setPatientField("bookingToken", result.bookingToken);
        if (result.groupName) {
          setPatientField("groupName", result.groupName);
        }
        funnelTracker.setHealthieId(result.id);
        funnelTracker.trackPatientCreated(result.id);
        navigate(terminalRoute);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.error?.message || "Something went wrong",
        );
      }
      return;
    }

    syncDraft();
    setCurrentStepIndex(currentStepIndex + 1);
  };

  const onPrev = () => {
    if (currentStepIndex === 0) {
      setShowForm(false);
    }
    setCurrentStepIndex(Math.max(currentStepIndex - 1, 0));
  };

  const handleStart = () => {
    funnelTracker.trackFunnelStart();
    reset();
    useFunnelStore.getState().resetSession();
    setShowForm(true);
  };

  const CurrentStep = formSteps[currentStepIndex];

  return (
    <MainLayout>
      <Toaster containerClassName="mt-20" />
      {!showForm && renderIntro(handleStart)}
      {showForm && <CurrentStep.component onPrev={onPrev} onNext={onNext} />}
    </MainLayout>
  );
};
